import { Input } from '@components/Input'
import {
	Autocomplete,
	type AutocompleteRenderInputParams,
	Box,
	FormLabel,
} from '@mui/material'
import { useCallback } from 'react'
import {
	type Control,
	Controller,
	type ControllerFieldState,
	type ControllerRenderProps,
	type FieldError,
	type FieldValues,
	type Path,
	type UseFormClearErrors,
	type UseFormGetValues,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export interface AutocompleteOptions {
	id: string
	name: string
}

const EMPTY_ARRAY = [] as const

const AutocompleteField = <T extends FieldValues = FieldValues>({
	field,
	error,
	disabled,
	supplierData,
	handleChange,
	handleInput,
	getOptionLabel,
	renderInput,
	getValues,
	fieldName,
}: {
	field: { value: string; onChange: (value: unknown) => void }
	error?: FieldError
	disabled: boolean
	supplierData: readonly AutocompleteOptions[]
	handleChange: (
		_: unknown,
		v: AutocompleteOptions | string | null,
		onChange: (value: unknown) => void
	) => void
	handleInput: (v: unknown, onChange: (value: unknown) => void) => void
	getOptionLabel: (option: AutocompleteOptions | string) => string
	renderInput: (
		params: AutocompleteRenderInputParams,
		error?: FieldError
	) => React.ReactElement
	getValues: UseFormGetValues<T>
	fieldName: Path<T>
}) => {
	const onChangeWrapper = useCallback(
		(_: unknown, v: AutocompleteOptions | string | null) =>
			handleChange(_, v, field.onChange),
		[handleChange, field.onChange]
	)
	const onInputWrapper = useCallback(
		(v: unknown) => handleInput(v, field.onChange),
		[handleInput, field.onChange]
	)
	const renderInputWrapper = useCallback(
		(params: AutocompleteRenderInputParams) => renderInput(params, error),
		[renderInput, error]
	)

	return (
		<Autocomplete
			fullWidth
			freeSolo
			disabled={disabled}
			options={supplierData || EMPTY_ARRAY}
			onChange={onChangeWrapper}
			onInput={onInputWrapper}
			getOptionLabel={getOptionLabel}
			value={
				supplierData.find(c => c?.id === field?.value)?.name ||
				(getValues()[fieldName] as string)
			}
			renderInput={renderInputWrapper}
		/>
	)
}

interface SupplierAutocompleteProps<
	T extends FieldValues = FieldValues,
	TName extends Path<T> = Path<T>,
> {
	control: Control<T>
	supplierData: readonly AutocompleteOptions[]
	disabled?: boolean
	clearErrors: UseFormClearErrors<T>
	getValues: UseFormGetValues<T>
	fieldName?: TName
}

export const SupplierAutocomplete = <
	T extends FieldValues = FieldValues,
	TName extends Path<T> = Path<T>,
>({
	control,
	supplierData,
	disabled = false,
	clearErrors,
	getValues,
	fieldName = 'supplierId' as TName,
}: SupplierAutocompleteProps<T, TName>) => {
	const { t } = useTranslation()

	const handleChange = useCallback(
		(
			_: unknown,
			v: AutocompleteOptions | string | null,
			onChange: (value: unknown) => void
		) => {
			clearErrors()
			onChange(typeof v === 'string' ? v : v?.id)
		},
		[clearErrors]
	)

	const handleInput = useCallback(
		(v: unknown, onChange: (value: unknown) => void) => {
			onChange(v)
		},
		[]
	)

	const getOptionLabel = useCallback(
		(option: AutocompleteOptions | string) =>
			typeof option === 'string' ? option : option.name,
		[]
	)

	const renderInput = useCallback(
		(params: AutocompleteRenderInputParams, error?: FieldError) => (
			<Input
				{...params}
				disabled={disabled}
				inputProps={{
					...params.inputProps,
					'data-testid': 'supplier-id-select',
				}}
				sx={{ minWidth: '280px', flex: 1, padding: 0 }}
				variant="outlined"
				placeholder={t('formField.selectSupplierOrEmail')}
				autoComplete="off"
				fieldError={error}
			/>
		),
		[disabled, t]
	)

	const renderAutomcomplete = useCallback(
		({
			field,
			fieldState,
		}: {
			field: ControllerRenderProps<T, TName>
			fieldState: ControllerFieldState
		}) => (
			<AutocompleteField
				field={field}
				error={fieldState.error}
				disabled={disabled}
				supplierData={supplierData}
				handleChange={handleChange}
				handleInput={handleInput}
				getOptionLabel={getOptionLabel}
				renderInput={renderInput}
				getValues={getValues}
				fieldName={fieldName}
			/>
		),
		[
			disabled,
			supplierData,
			handleChange,
			handleInput,
			getOptionLabel,
			renderInput,
			getValues,
			fieldName,
		]
	)

	return (
		<Box>
			<FormLabel>{t('productModal.supplierProductImportMethod')}</FormLabel>
			<Controller
				name={fieldName}
				control={control}
				rules={{ required: true }}
				render={renderAutomcomplete}
			/>
		</Box>
	)
}
